import React, { useMemo, useState } from 'react';
import { Search, ChevronDown, ChevronRight, X, Plus, Pencil, Trash2 } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Framework } from '../data/frameworks';
import { useFrameworks } from '../context/FrameworksContext';
import { CategoryManager } from './cms/CategoryManager';
interface SidebarProps {
  selectedId: string | null;
  onSelect: (id: string) => void;
  isOpen: boolean;
  onClose: () => void;
  isCmsMode: boolean;
  onAddFramework: () => void;
  onEditFramework: (id: string) => void;
}
export function Sidebar({
  selectedId,
  onSelect,
  isOpen,
  onClose,
  isCmsMode,
  onAddFramework,
  onEditFramework
}: SidebarProps) {
  const {
    frameworks,
    categories,
    deleteFramework
  } = useFrameworks();
  const [searchQuery, setSearchQuery] = useState('');
  const [collapsedCategories, setCollapsedCategories] = useState<Set<string>>(new Set());
  const toggleCategory = (category: string) => {
    const newCollapsed = new Set(collapsedCategories);
    if (newCollapsed.has(category)) {
      newCollapsed.delete(category);
    } else {
      newCollapsed.add(category);
    }
    setCollapsedCategories(newCollapsed);
  };
  const filteredFrameworks = useMemo(() => {
    if (!searchQuery.trim()) return frameworks;
    const query = searchQuery.toLowerCase();
    return frameworks.filter(f => f.name.toLowerCase().includes(query) || f.summary.toLowerCase().includes(query) || f.category.toLowerCase().includes(query));
  }, [searchQuery]);
  const frameworksByCategory = useMemo(() => {
    const grouped = new Map<string, Framework[]>();
    categories.forEach(c => grouped.set(c, []));
    filteredFrameworks.forEach(f => {
      const group = grouped.get(f.category);
      if (group) group.push(f);
    });
    return grouped;
  }, [filteredFrameworks, categories]);
  const handleDeleteFramework = (e: React.MouseEvent, id: string, name: string) => {
    e.stopPropagation();
    if (window.confirm(`Are you sure you want to delete "${name}"?`)) {
      deleteFramework(id);
      if (selectedId === id) {
        onSelect('');
      }
    }
  };
  const sidebarContent = <div className="h-full flex flex-col bg-slate-50 dark:bg-slate-900 border-r border-slate-200 dark:border-slate-800" data-id="element-229">
      {/* Mobile Header */}
      <div className="md:hidden flex items-center justify-between p-4 border-b border-slate-200 dark:border-slate-800" data-id="element-230">
        <span className="font-semibold text-slate-900 dark:text-white" data-id="element-231">
          Menu
        </span>
        <button onClick={onClose} className="p-2 text-slate-500 hover:bg-slate-200 dark:hover:bg-slate-800 rounded-md" data-id="element-232">
          <X className="w-5 h-5" data-id="element-233" />
        </button>
      </div>

      {/* Search */}
      <div className="p-4 border-b border-slate-200 dark:border-slate-800 sticky top-0 bg-slate-50 dark:bg-slate-900 z-10" data-id="element-234">
        <div className="relative" data-id="element-235">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" data-id="element-236" />
          <input type="text" placeholder="Search frameworks..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className="w-full pl-9 pr-4 py-2 bg-white dark:bg-slate-950 border border-slate-200 dark:border-slate-800 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 dark:text-slate-200 placeholder-slate-400" data-id="element-237" />
        </div>
      </div>

      {isCmsMode && <CategoryManager data-id="element-238" />}

      {/* Navigation List */}
      <div className="flex-1 overflow-y-auto p-3 space-y-4" data-id="element-239">
        {isCmsMode && <button onClick={onAddFramework} className="w-full flex items-center justify-center gap-2 px-3 py-2 text-sm font-medium text-blue-600 bg-blue-50 hover:bg-blue-100 dark:text-blue-400 dark:bg-blue-900/20 dark:hover:bg-blue-900/40 rounded-md transition-colors border border-blue-200 dark:border-blue-800/50" data-id="element-240">
            <Plus className="w-4 h-4" data-id="element-241" />
            Add Framework
          </button>}

        {categories.map(category => {
        const categoryFrameworks = frameworksByCategory.get(category) || [];
        if (categoryFrameworks.length === 0) return null;
        const isCollapsed = collapsedCategories.has(category);
        return <div key={category} className="space-y-1" data-id="element-242">
              <button onClick={() => toggleCategory(category)} className="w-full flex items-center justify-between px-2 py-1.5 text-xs font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wider hover:text-slate-700 dark:hover:text-slate-200 transition-colors" data-id="element-243">
                <span data-id="element-244">{category}</span>
                {isCollapsed ? <ChevronRight className="w-4 h-4" data-id="element-245" /> : <ChevronDown className="w-4 h-4" data-id="element-246" />}
              </button>

              <AnimatePresence initial={false} data-id="element-247">
                {!isCollapsed && <motion.div initial={{
              height: 0,
              opacity: 0
            }} animate={{
              height: 'auto',
              opacity: 1
            }} exit={{
              height: 0,
              opacity: 0
            }} transition={{
              duration: 0.2
            }} className="overflow-hidden space-y-0.5" data-id="element-248">
                    {categoryFrameworks.map(fw => <div key={fw.id} className="relative group" data-id="element-249">
                        <button onClick={() => {
                  onSelect(fw.id);
                  if (window.innerWidth < 768) onClose();
                }} className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${selectedId === fw.id ? 'bg-blue-100 text-blue-900 dark:bg-blue-900/40 dark:text-blue-100 font-medium' : 'text-slate-600 dark:text-slate-300 hover:bg-slate-200/50 dark:hover:bg-slate-800/50'} ${isCmsMode ? 'pr-16' : ''}`} data-id="element-250">
                          {fw.name}
                        </button>
                        {isCmsMode && <div className="absolute right-2 top-1/2 -translate-y-1/2 flex items-center gap-1 opacity-0 group-hover:opacity-100 transition-opacity" data-id="element-251">
                            <button onClick={e => {
                    e.stopPropagation();
                    onEditFramework(fw.id);
                  }} className="p-1.5 text-slate-400 hover:text-blue-600 dark:hover:text-blue-400 bg-white dark:bg-slate-800 rounded shadow-sm border border-slate-200 dark:border-slate-700" title="Edit" data-id="element-252">
                              <Pencil className="w-3 h-3" data-id="element-253" />
                            </button>
                            <button onClick={e => handleDeleteFramework(e, fw.id, fw.name)} className="p-1.5 text-slate-400 hover:text-red-600 dark:hover:text-red-400 bg-white dark:bg-slate-800 rounded shadow-sm border border-slate-200 dark:border-slate-700" title="Delete" data-id="element-254">
                              <Trash2 className="w-3 h-3" data-id="element-255" />
                            </button>
                          </div>}
                      </div>)}
                  </motion.div>}
              </AnimatePresence>
            </div>;
      })}

        {filteredFrameworks.length === 0 && <div className="text-center py-8 text-sm text-slate-500 dark:text-slate-400" data-id="element-256">
            No frameworks found.
          </div>}
      </div>
    </div>;
  return <>
      {/* Mobile Overlay */}
      <AnimatePresence data-id="element-257">
        {isOpen && <motion.div initial={{
        opacity: 0
      }} animate={{
        opacity: 1
      }} exit={{
        opacity: 0
      }} onClick={onClose} className="fixed inset-0 bg-slate-900/50 backdrop-blur-sm z-40 md:hidden" data-id="element-258" />}
      </AnimatePresence>

      {/* Sidebar Container */}
      <div className={`
        fixed inset-y-0 left-0 z-50 w-72 transform transition-transform duration-300 ease-in-out
        md:translate-x-0 md:static md:flex-shrink-0
        ${isOpen ? 'translate-x-0' : '-translate-x-full'}
      `} data-id="element-259">
        {sidebarContent}
      </div>
    </>;
}